import { useState, useEffect, RefObject, useCallback, memo } from 'react'
import styled from 'styled-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowDown } from '@fortawesome/free-solid-svg-icons'

const ScrollButton = styled.button`
  z-index: 2;
  position: absolute;
  right: 0px;
  top: -45px;
  width: 35px;
  height: 35px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.3s, transform 0.3s;

  &:hover {
    opacity: 0.9;
    transform: translateY(3px);
  }

  @media screen and (max-width: 1150px) {
    top: -40px;
  }
`

interface ScrollToBottomButtonProps {
  targetRef: RefObject<HTMLElement>
}

const ScrollToBottomButton = ({ targetRef }: ScrollToBottomButtonProps) => {
  const [isTargetVisible, setIsTargetVisible] = useState(true)
  const [hasIntersectionObserved, setHasIntersectionObserved] = useState(false)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsTargetVisible(entry.isIntersecting)
        setHasIntersectionObserved(true)
      },
      { threshold: 0.1 },
    )

    if (targetRef.current) {
      observer.observe(targetRef.current)
    }

    return () => {
      if (targetRef.current) {
        observer.unobserve(targetRef.current)
      }
    }
  }, [targetRef])

  const scrollToTarget = useCallback(() => {
    if (targetRef.current) {
      targetRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [targetRef])

  if (!hasIntersectionObserved || isTargetVisible) return null

  return (
    <ScrollButton onClick={scrollToTarget} aria-label='Scroll to bottom'>
      <FontAwesomeIcon icon={faArrowDown} size='lg' />
    </ScrollButton>
  )
}

export default memo(ScrollToBottomButton)
